import { useEffect, useState } from 'react';
import { useRxCollection } from 'rxdb-hooks';

import { useMst } from '../../mobx-models/Root';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import { RxdbCollectionName } from '../rxdbCollectionName';
import type { SignAndStampUserLevelCollection } from '../signAndStampUserLevel/signAndStampUserLevel.types';
import type {
  SignAndStampFile,
  SignAndStampFileCollection,
  SignAndStampFileDocument,
} from './signAndStamp.types';

export const useSignAndStampFile = () => {
  const { SiteDiscipline } = useMst();
  const signAndStampFileCollection: SignAndStampFileCollection = useRxCollection(
    RxdbCollectionName.SIGN_AND_STAMP_FILE,
  );

  const signAndStampUserLevelCollection: SignAndStampUserLevelCollection = useRxCollection(
    RxdbCollectionName.SIGN_AND_STAMP_USER_LEVEL,
  );

  const [signAndStampFileInitialized, setSignAndStampFileInitialized] = useState<boolean>(false);

  useEffect(() => {
    if (signAndStampFileCollection) {
      setSignAndStampFileInitialized(true);
    }
  }, [signAndStampFileCollection]);

  const insertIntoSignAndStampFileRxdb = async (fileObj: SignAndStampFile) => {
    try {
      if (fileObj.userId != 0 && fileObj.isSaveForFuture === true) {
        await signAndStampUserLevelCollection?.upsert(fileObj);
      } else if (fileObj.userId == 0 && fileObj.isSaveForFuture === false) {
        await signAndStampFileCollection?.upsert(fileObj);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(
        '🚀 ~ file: useSignAndStampFile.ts ~ line 32 ~ insertIntoSignAndStampFileRxdb ~ error',
        error,
      );
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useSignAndStampFile.tsx ~ line 32 ~ insertIntoSignAndStampFileRxdb ~',
      );
    }
  };

  /**
   * Subscribe to newly inserted attachments
   *
   * @param {(item: RxChangeEventInsert<SignAndStampFile>) => void} next
   * @return {*}
   */
  const subscribeToSignAndStampAttachments = (
    handleAttachments: (attachments: SignAndStampFile[]) => void,
  ) => {
    if (!signAndStampFileCollection) return;
    const query = signAndStampFileCollection.find();
    query.$.subscribe(handleAttachments);
  };

  const getSignAndStampFiles = async (): Promise<SignAndStampFileDocument[]> => {
    if (!signAndStampFileCollection) return [];
    let signAndStampFile: SignAndStampFileDocument[];
    try {
      signAndStampFile = await signAndStampFileCollection
        ?.find({
          selector: {
            isDelete: 'False',
          },
        })
        .exec();
      return signAndStampFile;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(
        '🚀 ~ file: useSignAndStampFile.ts ~ line 62 ~ getSignAndStampFile ~ error',
        error,
      );
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useSignAndStampFile.tsx ~ line 62 ~ getSignAndStampFiles ~',
      );
      throw new Error(`error ${error}`);
    }
  };

  const updateSignAndStampFile = async (
    fileUUId: string,
    name: string,
    showOnReport: boolean,
    fileDescription: string,
    isDelete: string,
  ): Promise<SignAndStampFileDocument[] | undefined> => {
    if (!signAndStampFileCollection) return [];
    try {
      if (signAndStampFileCollection) {
        const updated = await signAndStampFileCollection
          .findOne({
            selector: {
              fileAimsUUID: fileUUId,
            },
          })
          .update({
            $set: {
              fileName: name,
              showOnReport,
              fileDescription,
              isDelete,
              fkAimsfileTblSp: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
              userModified: '',
            },
          });
        return updated;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(
        '🚀 ~ file: useSignAndStampFile.ts ~ line 90 ~ updateSignAndStampFile ~ error',
        error,
      );
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useSignAndStampFile.tsx ~ line 90 ~ updateSignAndStampFile ~',
      );
      throw new Error(`error ${error}`);
    }
  };

  const deleteSignAndStampFile = async (
    fileUUId: string,
    isDelete: string,
  ): Promise<SignAndStampFileDocument[] | undefined> => {
    if (!signAndStampUserLevelCollection) return;
    try {
      if (signAndStampUserLevelCollection) {
        const updated = await signAndStampUserLevelCollection
          .findOne({
            selector: {
              fileUUID: fileUUId,
            },
          })
          .update({
            $set: {
              isDelete,
            },
          });
        return updated;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(
        '🚀 ~ file: useSignAndStampFile.ts ~ line 120 ~ deleteSignAndStampFile ~ error',
        error,
      );
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useSignAndStampFile.tsx ~ line 120 ~ deleteSignAndStampFile ~',
      );
      throw new Error(`error ${error}`);
    }
  };

  const getAllSignAndStampFiles = async (): Promise<SignAndStampFileDocument[]> => {
    if (!signAndStampFileCollection) return [];
    let signAndStampFile: SignAndStampFileDocument[];
    try {
      const site = SiteDiscipline.siteId;
      const discipline = SiteDiscipline.disciplineId;
      const queryObj = {
        selector: { $and: [{ siteId: site }, { disciplineId: discipline }] },
      };

      signAndStampFile = await signAndStampFileCollection?.find(queryObj).exec();
      return signAndStampFile;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(
        '🚀 ~ file: useSignAndStampFile.ts ~ line 147 ~ getAllSignAndStampFiles ~ error',
        error,
      );
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: useSignAndStampFile.tsx ~ line 147 ~ getAllSignAndStampFiles ~',
      );
      throw new Error(`error ${error}`);
    }
  };

  return {
    signAndStampFileInitialized,
    signAndStampFileCollection,
    getSignAndStampFiles,
    insertIntoSignAndStampFileRxdb,
    subscribeToSignAndStampAttachments,
    updateSignAndStampFile,
    deleteSignAndStampFile,
    getAllSignAndStampFiles,
  };
};

export default useSignAndStampFile;
